import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PartnerMenu from "../layouts/partner_sidemenu";
import PartneHeader from "../layouts/partner_header";
import DashboardFooter from "../../institutions/layouts/dashboard_footer";
import Canvas from "../../institutions/layouts/dahboard_canvas";
import axios from "axios";

type Category = { id: number; name: string; slug: string };
type Clinic = { id: number; name: string };
type ClinicService = {
  id: number;
  service_id: number;
  service_name: string;
  service_title: string;
  service_subtitle: string;
  service_category: string;
  minimum_rate: number;
  hourly_rate: number;
  is_billable: boolean;
  clinic_id: number;
  clinic_name: string;
};
type ServiceRequest = {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  creator?: { id?: number | null; name?: string | null };
  request_type?: string;
};

const ProfessionServices: React.FC = () => {
  const { professionSlug } = useParams();
  const baseUrl = (import.meta as any).env?.VITE_API_URL || (process as any).env?.REACT_APP_API_URL || "http://64.227.174.0:8004";

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [services, setServices] = useState<ClinicService[]>([]);
  const [generalRequests, setGeneralRequests] = useState<ServiceRequest[]>([]);
  const [privateRequests, setPrivateRequests] = useState<ServiceRequest[]>([]);

  const partnerName: any = "";
  const partnerId: any = "";
  const partnerCat: any = "";
  const partnerLogo: any = "";

  const clinicIds = useMemo(() => clinics.map(c => c.id), [clinics]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!professionSlug) return;
      setLoading(true);
      setError(null);
      try {
        // 1) Resolve category by slug
        const catsResp = await axios.get(`${baseUrl}/facilities/categories-with-slugs`);
        if (catsResp.data?.status !== "success") throw new Error(catsResp.data?.message || "Failed to load categories");
        const cats: any[] = catsResp.data.data || [];
        const found = cats.find((c: any) => c.slug === professionSlug);
        if (!found) throw new Error("Unknown profession");
        if (mounted) setCategory({ id: found.id, name: found.name, slug: found.slug });

        // 2) Clinics in this category
        const clinicsResp = await axios.post(`${baseUrl}/facilities/clinics`, { service_id: found.id });
        if (clinicsResp.data?.status !== "success") throw new Error(clinicsResp.data?.message || "Failed to load clinics");
        const clinicList: Clinic[] = (clinicsResp.data.data || []).map((cl: any) => ({ id: cl.clinic_id, name: cl.clinic_name }));
        if (mounted) setClinics(clinicList);

        // 3) Fetch services per clinic
        const svcResults: ClinicService[] = [];
        for (const cl of clinicList) {
          try {
            const svcResp = await axios.get(`${baseUrl}/facilities/services/clinic-services/${cl.id}/`);
            if (svcResp.data?.status === "success") {
              const arr = (svcResp.data?.data?.services || []).map((s: any) => ({
                ...s,
                clinic_id: svcResp.data?.data?.clinic_id,
                clinic_name: svcResp.data?.data?.clinic_name,
              })) as ClinicService[];
              svcResults.push(...arr);
            }
          } catch (e) {
            // continue
          }
        }
        if (mounted) setServices(svcResults);

        const serviceIds = Array.from(new Set<number>(svcResults.map((service) => service.service_id).filter(Boolean as any)));

        // 4) Fetch general and private requests for this profession
        const [genResp, privResp] = await Promise.all([
          axios.get(`${baseUrl}/collaborations/general-requests`, {
            params: {
              page_size: 50,
              ...(serviceIds.length ? { service_ids: serviceIds.join(',') } : {})
            }
          }),
          axios.get(`${baseUrl}/collaborations/private-requests`, { params: { page_size: 50 } })
        ]);
        const genData: ServiceRequest[] = genResp.data?.data?.requests || [];
        const privData: ServiceRequest[] = (privResp.data?.data?.requests || []).filter((r: any) => clinicList.some(c => c.id === (r.creator?.id || null)));
        if (mounted) {
          setGeneralRequests(genData);
          setPrivateRequests(privData);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load services");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [professionSlug, baseUrl]);

  return (
    <>
      <PartneHeader partner_name={partnerName} partner_id={partnerId} partner_cat={partnerCat} partner_logo={partnerLogo} />
      <PartnerMenu />

      <div className="vertical-overlay"></div>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0">{category?.name || professionSlug} - Services</h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item"><a href="/partner/home">Partner</a></li>
                      <li className="breadcrumb-item active">Services</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {loading && (
              <div className="alert alert-info">Loading services...</div>
            )}
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            <div className="row mt-4">
              <div className="col">
                <div className="card card-animate">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Available Services</h6>
                    <span className="badge bg-primary-subtle text-primary">{services.length}</span>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Clinic</th>
                            <th>Category</th>
                            <th>Min Rate</th>
                            <th>Hourly</th>
                            <th>Billable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {services.slice(0, 50).map((s) => (
                            <tr key={`${s.id}-${s.clinic_id}`}>
                              <td>
                                <div className="fw-semibold">{s.service_title || s.service_name}</div>
                                <div className="text-muted small">{s.service_subtitle}</div>
                              </td>
                              <td>{s.clinic_name}</td>
                              <td>{s.service_category}</td>
                              <td>{s.minimum_rate}</td>
                              <td>{s.hourly_rate}</td>
                              <td>{s.is_billable ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                          {services.length === 0 && (
                            <tr>
                              <td colSpan={6} className="text-muted">No services found for this profession.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12 col-lg-6">
                <div className="card card-animate h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">General Requests</h6>
                    <span className="badge bg-success-subtle text-success">{generalRequests.length}</span>
                  </div>
                  <div className="card-body">
                    {generalRequests.slice(0, 10).map(req => (
                      <div key={req.id} className="mb-3">
                        <div className="fw-semibold">{req.title}</div>
                        <div className="text-muted small">{req.description}</div>
                        <div className="text-muted small">Clinic: {req.creator?.name || 'N/A'} · Status: {req.status} · Priority: {req.priority}</div>
                      </div>
                    ))}
                    {generalRequests.length === 0 && (
                      <div className="text-muted">No general requests for this profession.</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6 mt-3 mt-lg-0">
                <div className="card card-animate h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Private Requests</h6>
                    <span className="badge bg-warning-subtle text-warning">{privateRequests.length}</span>
                  </div>
                  <div className="card-body">
                    {privateRequests.slice(0, 10).map(req => (
                      <div key={req.id} className="mb-3">
                        <div className="fw-semibold">{req.title}</div>
                        <div className="text-muted small">{req.description}</div>
                        <div className="text-muted small">Clinic: {req.creator?.name || 'N/A'} · Status: {req.status} · Priority: {req.priority}</div>
                      </div>
                    ))}
                    {privateRequests.length === 0 && (
                      <div className="text-muted">No private requests for this profession.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <DashboardFooter />
      </div>

      <Canvas />
    </>
  );
};

export default ProfessionServices;
